import { useState, useMemo } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useFizbo, type FizboListing } from '@/hooks/useFizbo';
import AddFizboModal from './components/AddFizboModal';
import FizboDetailModal from './components/FizboDetailModal';
import AkilliArama from './components/AkilliArama';

const statusColors: Record<string, string> = {
  Kabul: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  Düşünecek: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  Red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'Başka Emlakçıda': 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
};

export default function FizboPage() {
  const { isDark } = useTheme();
  const { listings, isLoading, addListing } = useFizbo();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Tümü');
  const [typeFilter, setTypeFilter] = useState('Tümü');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState<FizboListing | null>(null);

  const filteredListings = useMemo(() => {
    return listings.filter((item) => {
      const matchesSearch =
        !search ||
        item.ownerName.toLowerCase().includes(search.toLowerCase()) ||
        item.location.toLowerCase().includes(search.toLowerCase()) ||
        item.listingNo.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'Tümü' || item.status === statusFilter;
      const matchesType = typeFilter === 'Tümü' || item.propertyType === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [listings, search, statusFilter, typeFilter]);

  const [showAkilliArama, setShowAkilliArama] = useState(false);

  const handleAdd = async (formData: {
    ownerName: string;
    phone: string;
    propertyType: string;
    transactionType: string;
    location: string;
    status: string;
    notes: string;
    price?: number;
    sqm?: number;
    roomCount?: string;
    listingDate?: string;
    reminderOption?: string;
    freeReminder?: { date: string; task: string };
  }) => {
    await addListing(formData);
    setShowAddModal(false);
  };

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-primary-500`}>
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-line text-white text-lg" />
            </div>
          </div>
          <div>
            <h1 className={`text-lg font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              FİZBO Takip
            </h1>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              {filteredListings.length} kayıt
            </p>
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => setShowAkilliArama(!showAkilliArama)}
            className={`flex-1 sm:flex-none py-2.5 px-4 font-medium text-sm rounded-lg transition-colors flex items-center justify-center gap-2 border ${
              showAkilliArama
                ? 'bg-primary-500 text-white border-primary-500'
                : isDark
                ? 'border-dark-border text-dark-text hover:bg-dark-border'
                : 'border-light-border text-light-text hover:bg-light-bg'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-flashlight-line" />
            </div>
            Akıllı Arama
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex-1 sm:flex-none py-2.5 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line" />
            </div>
            Yeni FİZBO Ekle
          </button>
        </div>
      </div>

      {/* Akıllı Arama Panel */}
      {showAkilliArama && (
        <div className={`rounded-xl p-4 border ${isDark ? 'bg-dark-card border-dark-border' : 'bg-white border-light-border'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-flashlight-line text-primary-500" />
              </div>
              <p className={`text-sm font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                Akıllı Arama Ön Bilgi
              </p>
            </div>
            <button
              onClick={() => setShowAkilliArama(false)}
              className={`w-6 h-6 flex items-center justify-center rounded cursor-pointer ${isDark ? 'text-dark-muted hover:text-dark-text' : 'text-light-muted hover:text-light-text'}`}
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
          <AkilliArama />
        </div>
      )}

      {/* Filters */}
      <div className={`rounded-xl p-3 md:p-4 ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border flex-1 ${
            isDark
              ? 'bg-dark-bg border-dark-border'
              : 'bg-light-bg border-light-border'
          }`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-search-line text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ara (isim, lokasyon, ilan no...)"
              className={`flex-1 bg-transparent text-sm outline-none ${
                isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'
              }`}
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`px-3 py-2 rounded-lg text-sm border outline-none ${
              isDark
                ? 'bg-dark-bg border-dark-border text-dark-text'
                : 'bg-light-bg border-light-border text-light-text'
            }`}
          >
            <option value="Tümü">Tüm Durumlar</option>
            <option value="Kabul">Kabul</option>
            <option value="Düşünecek">Düşünecek</option>
            <option value="Red">Red</option>
            <option value="Başka Emlakçıda">Başka Emlakçıda</option>
          </select>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={`px-3 py-2 rounded-lg text-sm border outline-none ${
              isDark
                ? 'bg-dark-bg border-dark-border text-dark-text'
                : 'bg-light-bg border-light-border text-light-text'
            }`}
          >
            <option value="Tümü">Tüm Mülk Türleri</option>
            <option value="Daire">Daire</option>
            <option value="Villa">Villa</option>
            <option value="Dükkan">Dükkan</option>
            <option value="Arsa">Arsa</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
          <div className="w-8 h-8 mx-auto mb-3 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
          <p className={`text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            İlanlar yükleniyor...
          </p>
        </div>
      )}

      {/* Table */}
      {!isLoading && (
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left text-xs font-medium ${isDark ? 'bg-dark-bg text-dark-muted' : 'bg-light-bg text-light-muted'}`}>
                  <th className="px-4 py-3">İlan No</th>
                  <th className="px-4 py-3">Ad Soyad</th>
                  <th className="px-4 py-3">Telefon</th>
                  <th className="px-4 py-3">Mülk Türü</th>
                  <th className="px-4 py-3">İşlem</th>
                  <th className="px-4 py-3">Lokasyon</th>
                  <th className="px-4 py-3">Durum</th>
                  <th className="px-4 py-3">Gün</th>
                  <th className="px-4 py-3">Yorgunluk</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className={`text-sm ${isDark ? 'divide-y divide-dark-border' : 'divide-y divide-light-border'}`}>
                {filteredListings.map((item) => (
                  <tr
                    key={item.id}
                    className={`transition-colors cursor-pointer ${
                      isDark ? 'hover:bg-dark-border/50' : 'hover:bg-light-bg'
                    }`}
                    onClick={() => setSelectedListing(item)}
                  >
                    <td className={`px-4 py-3 font-mono text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                      {item.listingNo}
                    </td>
                    <td className={`px-4 py-3 font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                      {item.ownerName}
                    </td>
                    <td className={`px-4 py-3 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                      {item.phone}
                    </td>
                    <td className={`px-4 py-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                      {item.propertyType}
                    </td>
                    <td className={`px-4 py-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                      {item.transactionType}
                    </td>
                    <td className={`px-4 py-3 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                      {item.location}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusColors[item.status]}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className={`px-4 py-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                      {item.daysOld}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-16 h-1.5 rounded-full ${isDark ? 'bg-dark-border' : 'bg-light-border'}`}>
                          <div
                            className={`h-full rounded-full ${
                              item.fatigueScore < 30 ? 'bg-green-500' :
                              item.fatigueScore < 60 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${item.fatigueScore}%` }}
                          />
                        </div>
                        <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                          %{item.fatigueScore}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                        isDark ? 'hover:bg-dark-border text-dark-muted' : 'hover:bg-light-border text-light-muted'
                      }`}>
                        <i className="ri-more-2-fill" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y dark:divide-dark-border divide-light-border">
            {filteredListings.map((item) => (
              <div
                key={item.id}
                className={`p-4 cursor-pointer transition-colors ${
                  isDark ? 'hover:bg-dark-border/30' : 'hover:bg-light-bg'
                }`}
                onClick={() => setSelectedListing(item)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                      {item.ownerName}
                    </p>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                      {item.listingNo}
                    </p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusColors[item.status]}`}>
                    {item.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs mb-2">
                  <span className={isDark ? 'text-dark-muted' : 'text-light-muted'}>{item.phone}</span>
                  <span className={isDark ? 'text-dark-muted' : 'text-light-muted'}>{item.propertyType}</span>
                  <span className={isDark ? 'text-dark-muted' : 'text-light-muted'}>{item.transactionType}</span>
                </div>
                <p className={`text-xs mb-2 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                  {item.location}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                      {item.daysOld} gün
                    </span>
                    <div className={`w-12 h-1 rounded-full ${isDark ? 'bg-dark-border' : 'bg-light-border'}`}>
                      <div
                        className={`h-full rounded-full ${
                          item.fatigueScore < 30 ? 'bg-green-500' :
                          item.fatigueScore < 60 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${item.fatigueScore}%` }}
                      />
                    </div>
                  </div>
                  <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                    %{item.fatigueScore}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredListings.length === 0 && (
        <div className={`text-center py-12 rounded-xl ${isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'}`}>
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-dark-border flex items-center justify-center">
            <i className="ri-search-line text-2xl text-gray-400 dark:text-dark-muted" />
          </div>
          <p className={`text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            Sonuç bulunamadı
          </p>
        </div>
      )}

      {/* Modals */}
      {showAddModal && <AddFizboModal onClose={() => setShowAddModal(false)} onAdd={handleAdd} />}
      {selectedListing && (
        <FizboDetailModal listing={selectedListing} onClose={() => setSelectedListing(null)} />
      )}
    </div>
  );
}